export type FontPair = {
  heading: string;
  body: string;
  /** Google Fonts query suffix, e.g. "Inter:wght@400;500;600;700" */
  headingHref?: string;
  bodyHref?: string;
};

export type Appearance = {
  logo_light_url?: string;     // shown on dark backgrounds
  logo_dark_url?: string;      // shown on light backgrounds
  logo_icon_url?: string;
  favicon_url?: string;
  apple_touch_icon_url?: string;

  colors?: {
    primary?: string;          // hex
    brand?: string;
    brand_2?: string;
    accent?: string;
    background_light?: string;
    background_dark?: string;
  };

  typography?: FontPair;

  custom_css?: string;

  header_preset?: "default" | "centered" | "minimal" | "split";
  footer_preset?: "default" | "compact" | "mega";

  default_theme?: "light" | "dark" | "system";
  allow_user_override?: boolean;
};

export const FONT_PAIRS: Record<string, FontPair> = {
  "inter-space-grotesk": {
    heading: "Space Grotesk",
    body: "Inter",
    headingHref: "Space+Grotesk:wght@500;600;700",
    bodyHref: "Inter:wght@400;500;600;700",
  },
  "outfit-figtree": {
    heading: "Outfit",
    body: "Figtree",
    headingHref: "Outfit:wght@500;600;700",
    bodyHref: "Figtree:wght@400;500;600;700",
  },
  "sora-manrope": {
    heading: "Sora",
    body: "Manrope",
    headingHref: "Sora:wght@500;600;700",
    bodyHref: "Manrope:wght@400;500;600;700",
  },
  "instrument-serif-work-sans": {
    heading: "Instrument Serif",
    body: "Work Sans",
    headingHref: "Instrument+Serif:ital@0;1",
    bodyHref: "Work+Sans:wght@400;500;600;700",
  },
  "dm-serif-fira-sans": {
    heading: "DM Serif Display",
    body: "Fira Sans",
    headingHref: "DM+Serif+Display",
    bodyHref: "Fira+Sans:wght@400;500;600;700",
  },
  "bebas-barlow": {
    heading: "Bebas Neue",
    body: "Barlow",
    headingHref: "Bebas+Neue",
    bodyHref: "Barlow:wght@400;500;600;700",
  },
};

export function buildAppearanceCss(a: Appearance): string {
  const c = a.colors ?? {};
  const decls: string[] = [];
  if (c.primary) {
    decls.push(`--primary: ${c.primary};`);
    decls.push(`--ring: ${c.primary};`);
  }
  if (c.brand) decls.push(`--brand: ${c.brand};`);
  if (c.brand_2) decls.push(`--brand-2: ${c.brand_2};`);
  if (c.accent) decls.push(`--accent: ${c.accent};`);
  if (c.brand || c.brand_2) {
    const b1 = c.brand ?? "var(--brand)";
    const b2 = c.brand_2 ?? "var(--brand-2)";
    decls.push(`--gradient-brand: linear-gradient(135deg, ${b1}, ${b2});`);
  }
  if (a.typography?.heading) {
    decls.push(`--font-display: "${a.typography.heading}", ui-sans-serif, system-ui, sans-serif;`);
  }
  if (a.typography?.body) {
    decls.push(`--font-sans: "${a.typography.body}", ui-sans-serif, system-ui, sans-serif;`);
  }

  const root = decls.length ? `:root, .dark { ${decls.join(" ")} }` : "";
  const lightBg = c.background_light ? `:root { --background: ${c.background_light}; }` : "";
  const darkBg = c.background_dark ? `.dark { --background: ${c.background_dark}; }` : "";
  return [root, lightBg, darkBg, a.custom_css ?? ""].filter(Boolean).join("\n");
}

export function googleFontsUrl(a: Appearance): string | null {
  const t = a.typography;
  if (!t) return null;
  const parts: string[] = [];
  if (t.headingHref) parts.push(`family=${t.headingHref}`);
  if (t.bodyHref) parts.push(`family=${t.bodyHref}`);
  if (!parts.length) return null;
  return `https://fonts.googleapis.com/css2?${parts.join("&")}&display=swap`;
}
